import { hasKeys, isDefined } from '@library/guards'
import type { Nullable } from '@library/types'
import { hasKeysLoose, ObjectEntries } from 'common/helpers'

export class ValidationError extends Error {
  constructor(public readonly state: ValidatorFullState<object>) {
    super('Валидация завершилась ошибкой')
  }

  static fromState(state: ValidatorFullState<object>) {
    return new ValidationError(state)
  }

  static isValidationError(candidate: unknown): candidate is ValidationError {
    return candidate instanceof ValidationError
  }
}

export class ValidationWarning extends Error {
  constructor(public readonly state: ValidatorFullState<object>) {
    super('Валидация завершилась ошибкой')
  }

  static fromState(state: ValidatorFullState<object>) {
    return new ValidationWarning(state)
  }

  static isValidationWarning(candidate: unknown): candidate is ValidationWarning {
    return candidate instanceof ValidationWarning
  }
}

const ValidationStatusArr = ['error', 'warning', 'successful'] as const
export type ValidationStatusType = (typeof ValidationStatusArr)[number] // | undefined
export type ValidationIndexingResult = {
  index: number
  message: string
  type: ValidationStatusType
}
export type ValidationResult = string | ValidationIndexingResult[] | null

type ValidatorFunction<T extends object, K extends keyof T> = (
  value: T[K],
  object: T,
) => ValidationResult | undefined

type ValidatorObject<T extends object> = {
  [Key in keyof T]?: ValidatorFunction<T, Key>
}

type ValidatorState<T extends object> = {
  [Key in keyof T]?: Nullable<string>
}

type ValidatorFullState<T extends object> = {
  items: ValidatorState<T>
  errorLevel: ValidationStatusType
}

export class Validator<T extends Record<string, unknown>> {
  private validationState: Map<keyof T, ValidationResult> = new Map()

  constructor(private readonly schema: ValidatorObject<T>) {}

  public static fromSchema<T extends Record<string, unknown>>(schema: ValidatorObject<T>) {
    return new Validator(schema)
  }

  public validate(candidate: T): ValidatorFullState<T> | undefined {
    this.validationState.clear()

    let statusLevel: ValidationStatusType = 'successful'

    for (const [key, value] of ObjectEntries(candidate)) {
      const validator = this.schema[key]
      if (validator === undefined) continue

      const message = validator(value, candidate)
      if (message === undefined) continue

      this.validationState.set(key, message)

      if (isValidationIndexingResult(message)) {
        statusLevel = getValidationStatusLevel(statusLevel, ...message.map(item => item.type))
      } else {
        statusLevel = 'error'
      }
    }

    const result = Object.fromEntries(this.validationState.entries())

    if (isValidationState<T>(result, Object.keys(candidate)) && this.validationState.size !== 0) {
      return {
        errorLevel: statusLevel,
        items: result,
      }
    }
  }
}

export function isValidationState<T extends Record<string, unknown>>(
  candidate: object,
  keys: string[],
): candidate is ValidatorState<T> {
  return hasKeysLoose(candidate, ...keys)
}

export const isValidationIndexingResult = (
  candidate?: ValidationResult,
): candidate is ValidationIndexingResult[] =>
  isDefined(candidate) &&
  Array.isArray(candidate) &&
  candidate.length > 0 &&
  hasKeys(candidate[0], 'index', 'message')

export const getValidationStatusLevel = (...levels: ValidationStatusType[]) => {
  return levels.reduce((result, current) => {
    switch (result) {
      case 'successful':
        return current
      case 'warning':
        if (current !== 'successful') return current

        return result
      default:
        return result
    }
  }, 'successful')
}
